import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import apiClient from '../../services/apiClient'; 
import { Title } from '../../components';
import Breadcrumb from '../../components/UI/Breadcrumb'; 

// API function for fetching subjects 
const fetchSubjects = async (category, department) => {
  const { data } = await apiClient.get('/study-material/subjects', {
    params: { category, department },
  });
  return data.data; 
};

// API function for adding a resource
const postNewResource = async (resourceData) => {
  const { data } = await apiClient.post('/study-material/resources', resourceData);
  return data;
};

// API function for adding tips
const patchNewTips = async ({ subjectId, tipsArray }) => {
  const { data } = await apiClient.patch(
    `/study-material/subjects/${subjectId}/tips`,
    { subjectId, tips: tipsArray }
  );
  return data;
};

// API function for fetching resources
const fetchResources = async (subjectId) => {
  const { data } = await apiClient.get(`/study-material/subjects/${subjectId}/resources`);
  return data.data;
};

// API function for updating a resource
const updateResourceAPI = async ({ resourceId, resourceData }) => {
  const { data } = await apiClient.patch(`/study-material/resources/${resourceId}`, resourceData);
  return data;
};

// API function for deleting a resource
const deleteResourceAPI = async (resourceId) => {
  const { data } = await apiClient.delete(`/study-material/resources/${resourceId}`);
  return data;
};

// Main Page Component 
const ManageContent = () => {
  // State for Subject Selection
  const [selectedCategory, setSelectedCategory] = useState('Semester Exams');
  const [selectedDepartment, setSelectedDepartment] = useState('CSE');
  const [selectedSubjectId, setSelectedSubjectId] = useState('');

  // Fix: Determine the correct department to fetch based on category
  const departmentToFetch = selectedCategory === 'Placements/Internships' 
    ? 'Common' 
    : selectedDepartment;

  // React Query for Fetching Subjects
  const {
    data: subjects,
    isLoading: isLoadingSubjects,
  } = useQuery({
    queryKey: ['subjects', selectedCategory, departmentToFetch], // Use fixed variable
    queryFn: () => fetchSubjects(selectedCategory, departmentToFetch), // Use fixed variable
    enabled: !!selectedCategory && (selectedCategory === 'Placements/Internships' || !!selectedDepartment), 
  });

  return (
    <>
      <Breadcrumb pageName="Manage Content" />
      <Title>Study Material</Title>

      {/* Subject Selector Section */}
      <div className="mb-6 rounded-sm border border-stroke bg-white p-8 shadow-default dark:border-strokedark dark:bg-boxdark">
        <h3 className="mb-6 text-center text-2xl font-bold text-black dark:text-white">
          Select Subject to Manage
        </h3>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {/* Category Filter */}
          <div className="flex flex-col">
            <label className="mb-2 block text-sm font-medium text-black dark:text-white">Category</label>
            <select
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                setSelectedSubjectId(''); 
              }}
              className="w-full rounded border border-stroke bg-transparent px-5 py-3 text-black outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary"
            >
              <option value="Semester Exams">Semester Exams</option>
              <option value="Placements/Internships">Placements/Internships</option>
            </select>
          </div>

          {/* Department Filter (Conditional) */}
          <div className="flex flex-col">
            <label className="mb-2 block text-sm font-medium text-black dark:text-white">Department</label>
            <select
              value={selectedDepartment}
              onChange={(e) => {
                setSelectedDepartment(e.target.value);
                setSelectedSubjectId(''); 
              }}
              disabled={selectedCategory !== 'Semester Exams'}
              className="w-full rounded border border-stroke bg-transparent px-5 py-3 text-black outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary disabled:opacity-50"
            >
              <option value="CSE">CSE</option>
              <option value="AI">AI</option>
              <option value="Common">Common</option>
            </select>
          </div>

          {/* Subject List Dropdown */}
          <div className="flex flex-col">
            <label className="mb-2 block text-sm font-medium text-black dark:text-white">Subject</label>
            <select
              value={selectedSubjectId}
              onChange={(e) => setSelectedSubjectId(e.target.value)}
              disabled={isLoadingSubjects || !subjects || subjects.length === 0}
              className="w-full rounded border border-stroke bg-transparent px-5 py-3 text-black outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary disabled:opacity-50"
            >
              <option value="">
                {isLoadingSubjects
                  ? 'Loading subjects...'
                  : 'Select a subject'}
              </option>
              {subjects &&
                subjects.map((subject) => (
                  <option key={subject._id} value={subject._id}>
                    {subject.subjectName}
                  </option>
                ))}
            </select>
          </div>
        </div>
      </div>

      {/* Forms Section */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <AddResourceForm subjectId={selectedSubjectId} />
        <AddTipsForm subjectId={selectedSubjectId} />
      </div>

      <ResourceList subjectId={selectedSubjectId} />
    </>
  );
};

// Add Resource Form Component
const AddResourceForm = ({ subjectId }) => {
  const queryClient = useQueryClient();

  // Form State 
  const [title, setTitle] = useState('');
  const [link, setLink] = useState('');
  const [subCategory, setSubCategory] = useState('Notes');
  const [resourceType, setResourceType] = useState('Link');

  // React Query Mutation 
  const mutation = useMutation({
    mutationFn: postNewResource,
    onSuccess: (data) => {
      toast.success(`Resource "${data.data.title}" added!`);
      // Reset form
      setTitle('');
      setLink('');
      setSubCategory('Notes');
      setResourceType('Link');
      queryClient.invalidateQueries({ queryKey: ['resources', subjectId] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Error adding resource');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    mutation.mutate({
      title,
      link,
      subCategory,
      resourceType,
      subjectId, 
    });
  };

  return (
    <div className={`rounded-sm border border-stroke bg-white p-8 shadow-default dark:border-strokedark dark:bg-boxdark ${!subjectId ? 'opacity-50' : ''}`}>
      <h3 className="mb-6 text-center text-2xl font-bold text-black dark:text-white">
        Add Resource
      </h3>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Title */}
        <div className="flex flex-col">
          <label className="mb-2 block text-sm font-medium text-black dark:text-white">Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., Unit 1 Notes PDF"
            required
            disabled={!subjectId}
            className="w-full rounded border border-stroke bg-transparent px-5 py-3 text-black outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary disabled:opacity-50"
          />
        </div>

        {/* Link */}
        <div className="flex flex-col">
          <label className="mb-2 block text-sm font-medium text-black dark:text-white">Link (URL or PDF Link)</label>
          <input
            type="url"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            placeholder="https://..."
            required
            disabled={!subjectId}
            className="w-full rounded border border-stroke bg-transparent px-5 py-3 text-black outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary disabled:opacity-50"
          />
        </div>

        <div className="grid grid-cols-2 gap-6">
          {/* Sub-Category (from your schema) */}
          <div className="flex flex-col">
            <label className="mb-2 block text-sm font-medium text-black dark:text-white">Sub-Category</label>
            <select
              value={subCategory}
              onChange={(e) => setSubCategory(e.target.value)}
              disabled={!subjectId}
              className="w-full rounded border border-stroke bg-transparent px-5 py-3 text-black outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary disabled:opacity-50"
            >
              <option>Notes</option>
              <option>Important topics</option>
              <option>Youtube Resources</option>
              <option>PYQs</option>
              <option>Other</option>
            </select>
          </div>

          {/* Resource Type (from your schema) */}
          <div className="flex flex-col">
            <label className="mb-2 block text-sm font-medium text-black dark:text-white">Resource Type</label>
            <select
              value={resourceType}
              onChange={(e) => setResourceType(e.target.value)}
              disabled={!subjectId}
              className="w-full rounded border border-stroke bg-transparent px-5 py-3 text-black outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary disabled:opacity-50"
            >
              <option>Link</option>
              <option>PDF</option>
            </select>
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={!subjectId || mutation.isPending}
          className="flex w-full justify-center rounded bg-primary p-3 font-medium text-gray hover:bg-opacity-90 disabled:opacity-50"
        >
          {mutation.isPending ? 'Adding Resource...' : 'Add Resource'}
        </button>
      </form>
    </div>
  );
};

// Add Tips Form Component 
const AddTipsForm = ({ subjectId }) => {
  const [tipsText, setTipsText] = useState('');

  //  React Query Mutation 
  const mutation = useMutation({
    mutationFn: patchNewTips,
    onSuccess: () => {
      toast.success('Tips added successfully!');
      setTipsText(''); // Clear textarea
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Error adding tips');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Convert textarea string into an array, filtering out empty lines
    const tipsArray = tipsText.split('\n').filter(tip => tip.trim().length > 0);
    
    if (tipsArray.length === 0) {
      toast.error('Please enter at least one tip.');
      return;
    }

    mutation.mutate({ subjectId, tipsArray });
  };

  return (
    <div className={`rounded-sm border border-stroke bg-white p-8 shadow-default dark:border-strokedark dark:bg-boxdark ${!subjectId ? 'opacity-50' : ''}`}>
      <h3 className="mb-6 text-center text-2xl font-bold text-black dark:text-white">
        Add Tips
      </h3>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Tips Textarea */}
        <div className="flex flex-col">
          <label className="mb-2 block text-sm font-medium text-black dark:text-white">
            Tips <span className="text-xs font-normal text-body">(Press Enter for a new tip)</span>
          </label>
          <textarea
            value={tipsText}
            onChange={(e) => setTipsText(e.target.value)} // <-- This was the typo
            rows={10}
            placeholder="e.g., Focus on Chapter 2 for midterms."
            required
            disabled={!subjectId}
            className="w-full rounded border border-stroke bg-transparent px-5 py-3 text-black outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary disabled:opacity-50"
          />
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={!subjectId || mutation.isPending}
          className="flex w-full justify-center rounded bg-primary p-3 font-medium text-gray hover:bg-opacity-90 disabled:opacity-50"
        >
          {mutation.isPending ? 'Adding Tips...' : 'Add Tips'}
        </button>
      </form>
    </div>
  );
};

export default ManageContent;

// Resource List Component
const ResourceList = ({ subjectId }) => {
  const queryClient = useQueryClient();
  const [editingResource, setEditingResource] = useState(null);

  const { data: resources, isLoading } = useQuery({
    queryKey: ['resources', subjectId],
    queryFn: () => fetchResources(subjectId),
    enabled: !!subjectId,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteResourceAPI,
    onSuccess: () => {
      toast.success('Resource deleted successfully!');
      queryClient.invalidateQueries({ queryKey: ['resources', subjectId] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Error deleting resource');
    },
  });

  const updateMutation = useMutation({
    mutationFn: updateResourceAPI,
    onSuccess: (data) => {
      toast.success('Resource updated successfully!');
      setEditingResource(null);
      queryClient.invalidateQueries({ queryKey: ['resources', subjectId] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Error updating resource');
    },
  });

  if (!subjectId) return null;

  return (
    <div className="mt-6 rounded-sm border border-stroke bg-white p-8 shadow-default dark:border-strokedark dark:bg-boxdark">
      <h3 className="mb-6 text-center text-2xl font-bold text-black dark:text-white">
        Manage Existing Resources
      </h3>
      {isLoading ? (
        <p className="text-center font-medium">Loading resources...</p>
      ) : !resources || resources.length === 0 ? (
        <p className="text-center font-medium">No resources found for this subject.</p>
      ) : (
        <div className="flex flex-col gap-4">
          {resources.map((resource) => (
            <div key={resource._id} className="rounded border border-stroke p-4 dark:border-strokedark">
              {editingResource && editingResource._id === resource._id ? (
                <form 
                  onSubmit={(e) => {
                    e.preventDefault();
                    updateMutation.mutate({ resourceId: resource._id, resourceData: editingResource });
                  }}
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <input 
                      type="text" 
                      value={editingResource.title} 
                      onChange={(e) => setEditingResource({...editingResource, title: e.target.value})} 
                      className="w-full rounded border border-stroke bg-transparent px-3 py-2 text-black outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input dark:text-white"
                      placeholder="Title" required
                    />
                    <input 
                      type="url" 
                      value={editingResource.link} 
                      onChange={(e) => setEditingResource({...editingResource, link: e.target.value})} 
                      className="w-full rounded border border-stroke bg-transparent px-3 py-2 text-black outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input dark:text-white"
                      placeholder="URL" required
                    />
                    <select
                      value={editingResource.subCategory}
                      onChange={(e) => setEditingResource({...editingResource, subCategory: e.target.value})}
                      className="w-full rounded border border-stroke bg-transparent px-3 py-2 text-black outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input dark:text-white"
                    >
                      <option>Notes</option>
                      <option>Important topics</option>
                      <option>Youtube Resources</option>
                      <option>PYQs</option>
                      <option>Other</option>
                    </select>
                    <select
                      value={editingResource.resourceType}
                      onChange={(e) => setEditingResource({...editingResource, resourceType: e.target.value})}
                      className="w-full rounded border border-stroke bg-transparent px-3 py-2 text-black outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input dark:text-white"
                    >
                      <option>Link</option>
                      <option>PDF</option>
                    </select>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => setEditingResource(null)} className="rounded px-4 py-2 font-medium border border-stroke dark:border-strokedark text-black hover:bg-opacity-90 dark:text-white">Cancel</button>
                    <button type="submit" disabled={updateMutation.isPending} className="rounded bg-primary px-4 py-2 font-medium text-white hover:bg-opacity-90">{updateMutation.isPending ? 'Saving...' : 'Save'}</button>
                  </div>
                </form>
              ) : (
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <h4 className="text-lg font-bold text-black dark:text-white">{resource.title}</h4>
                    <p className="text-sm font-medium text-body">{resource.subCategory} • {resource.resourceType}</p>
                    <a href={resource.link} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline truncate block max-w-full sm:max-w-md">{resource.link}</a>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => setEditingResource(resource)} className="rounded bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-opacity-90">Edit</button>
                    <button onClick={() => deleteMutation.mutate(resource._id)} disabled={deleteMutation.isPending && deleteMutation.variables === resource._id} style={{ backgroundColor: '#dc3545' }} className="rounded px-4 py-2 text-sm font-medium text-white hover:bg-opacity-90">{deleteMutation.isPending && deleteMutation.variables === resource._id ? 'Deleting...' : 'Delete'}</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};